/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ActionList,
  Badge,
  Button,
  Frame,
  Icon,
  Navigation,
  Tooltip,
  TopBar,
  Modal,
  BlockStack,
  Text,
  Spinner,
} from "@shopify/polaris";
import {
  ExitIcon,
  LiveIcon,
  LockIcon,
  OrderIcon,
  ProfileIcon,
  SettingsIcon,
  PersonSegmentIcon,
  ChartVerticalIcon,
  ContractIcon,
  ReceiptPaidIcon,
  QuestionCircleIcon,
  CursorOptionIcon,
  FileIcon,
  ReplaceIcon,
  StoreIcon,
  StarIcon,
  ExportIcon,
} from "@shopify/polaris-icons";
import { useState, useCallback, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../store/store/slices/AuthSlice";
import ToastAlert from "../atoms/ToastComp";
import { useGetUserStoresQuery, useSwitchStoreMutation } from "../../store/store/slices/UsersRTK";
import { showToast } from "../../store/store/slices/AlertSlice";

export default function LayoutWrapper() {
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const navigateTo = useNavigate();
  const dispatch = useDispatch();
  const pageLocation = useLocation();
  const [searchActive, setSearchActive] = useState<boolean>(false);
  const userData = useSelector((state: any) => state.auth.user);
  const [userMenuActive, setUserMenuActive] = useState<boolean>(false);
  const [mobileNavigationActive, setMobileNavigationActive] =
    useState<boolean>(false);
  const [storeModalActive, setStoreModalActive] = useState<boolean>(false);

  const { data: stores = [], isLoading: storesLoading } = useGetUserStoresQuery({}, {
    skip: !storeModalActive,
  });
  const [switchStore, { isLoading: isSwitchingStore }] = useSwitchStoreMutation();

  const handleSearchResultsDismiss = useCallback(() => {
    setSearchActive(false);
  }, []);
  const toggleUserMenuActive = useCallback(
    () => setUserMenuActive((userMenuActive) => !userMenuActive),
    []
  );
  const onLogoutHandler = async (event: any) => {
    event.stopPropagation();
    dispatch(logout());
  };
  const toggleMobileNavigationActive = useCallback(
    () =>
      setMobileNavigationActive(
        (mobileNavigationActive) => !mobileNavigationActive
      ),
    []
  );

  const handleThemeChange = () => {
    const newValue = theme === "light" ? "dark" : "light";
    localStorage.setItem("theme", newValue);
    window.dispatchEvent(
      new CustomEvent("themeChanged", { detail: { value: newValue } })
    );
    // setTheme(newValue === "dark" ? "dark-experimental" : "light");

  };

  useEffect(() => {
    // listen for theme changes
    const handleThemeChange = (e: CustomEvent) => {
      setTheme(e.detail.value);
    };

    window.addEventListener("themeChanged", handleThemeChange as EventListener);
    return () =>
      window.removeEventListener(
        "themeChanged",
        handleThemeChange as EventListener
      );
  }, []);
  const READ_ACCOUNT_ACCESS =
    userData?.access_scopes?.includes("READ_ACCOUNTS");
  const READ_ORDERS_ACCESS = userData?.access_scopes?.includes("READ_ORDERS");
  const READ_SHOPIFY_ORDERS_ACCESS = userData?.access_scopes?.includes("READ_SHOPIFY_ORDERS");
  const READ_DASHBOARD_ACCESS =
    userData?.access_scopes?.includes("READ_ANALYTICS");
  const READ_SETTINGS_ACCESS =
    userData?.access_scopes?.includes("READ_SETTINGS");
  const TRACK_ORDERS_ACCESS =
    userData?.access_scopes?.includes("TRACK_ORDERS");
  const READ_FINANCE_ACCESS =
    userData?.access_scopes?.includes("READ_FINANCE");
  const READ_PAYMENT_ACCESS =
    userData?.access_scopes?.includes("READ_PAYMENT");

  const userMenuActions = [
    {
      items: [
        {
          content: (
            <h1
              style={{
                display: "flex",
                gap: ".5rem",
                alignItems: "center",
              }}
            >
              <div>
                <Icon source={ProfileIcon} />
              </div>
              <span>Profile</span>
            </h1>
          ) as any,
          onAction: () => {
            navigateTo("/dashboard/profile");
          },
        },
        {
          content: (
            <h1
              style={{
                display: "flex",
                gap: ".5rem",
                alignItems: "center",
              }}
            >
              <div>
                <Icon source={LiveIcon} />
              </div>
              <span>Accounts</span>
            </h1>
          ) as any,
          onAction: () => {
            navigateTo("/dashboard/accounts");
          },
          disbaled: !READ_ACCOUNT_ACCESS && userData?.role !== "ADMIN",
        },
        {
          content: (
            <h1
              style={{
                display: "flex",
                gap: ".5rem",
                alignItems: "center",
                width: "160px",
              }}
            >
              <div>
                <Icon source={ExitIcon} />
              </div>
              <span>Logout</span>
            </h1>
          ) as any,
          onAction: () => {
            dispatch(logout());
          },

        },
        {
          content: (
            <div
              style={{
                display: "flex",
                gap: ".5rem",
                alignItems: "center",
                width: "100%",
                border: "none",
                cursor: "pointer",
                padding: "0.5rem",
                borderRadius: "0.5rem",
                backgroundColor: theme === "dark" ? "#3a3a78" : "rgb(221 221 255)",

              }}
            >
              <div>
                <Icon source={ReplaceIcon} />
              </div>
              <span>Switch store</span>
            </div> as any
          ),
          onAction: () => {
            setStoreModalActive(true);
            setUserMenuActive(false);
          },
          disbaled: userData?.role === "ADMIN",
        }
      ],
    },
  ];

 
  const userMenuMarkup = (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginRight: ".5rem",
          gap: "1rem",
        }}
      >
        <Tooltip content="Need support?">
          {" "}
          <Button
            icon={QuestionCircleIcon}
            variant="monochromePlain"
            onClick={() => navigateTo("/dashboard/support")}
          ></Button>
        </Tooltip>
        <label
          className="theme-switch"
          style={{
            display: "inline-flex",
            alignItems: "center",
            ["--toggle-size" as any]: "11.25px",
          }}
        >
          <input type="checkbox" className="theme-switch__checkbox" checked={theme === "dark"} onChange={handleThemeChange} />
          <div className="theme-switch__container">
            <div className="theme-switch__clouds"></div>

            <div className="theme-switch__stars-container">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 144 55" fill="none">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M135.831 3.00688C135.055 3.85027 134.111 4.29946 133 4.35447C134.111 4.40947 135.055 4.85867 135.831 5.71123C136.607 6.55462 136.996 7.56303 136.996 8.72727C136.996 7.95722 137.172 7.25134 137.525 6.59129C137.886 5.93124 138.372 5.39954 138.98 5.00535C139.598 4.60199 140.268 4.39114 141 4.35447C139.88 4.2903 138.936 3.85027 138.16 3.00688C137.384 2.16348 136.996 1.16425 136.996 0C136.996 1.16425 136.607 2.16348 135.831 3.00688ZM31 23.3545C32.1114 23.2995 33.0551 22.8503 33.8313 22.0069C34.6075 21.1635 34.9956 20.1642 34.9956 19C34.9956 20.1642 35.3837 21.1635 36.1599 22.0069C36.9361 22.8503 37.8798 23.2903 39 23.3545C38.2679 23.3911 37.5976 23.602 36.9802 24.0053C36.3716 24.3995 35.8864 24.9312 35.5248 25.5913C35.172 26.2513 34.9956 26.9572 34.9956 27.7273C34.9956 26.563 34.6075 25.5546 33.8313 24.7112C33.0551 23.8587 32.1114 23.4095 31 23.3545Z"
                  fill="currentColor"
                />
              </svg>
            </div>

            <div className="theme-switch__circle-container">
              <div className="theme-switch__sun-moon-container">
                <div className="theme-switch__moon">
                  <div className="theme-switch__spot"></div>
                  <div className="theme-switch__spot"></div>
                  <div className="theme-switch__spot"></div>
                </div>
              </div>
            </div>
          </div>
        </label>


        {/* <label
          className="bb8-toggle"
          style={{ display: "inline-flex", alignItems: "center" }}
        >
          <input
            className="bb8-toggle__checkbox"
            type="checkbox"
            checked={theme === "dark"}
            onChange={handleThemeChange}
          />
          <div className="bb8-toggle__container">
            <div className="bb8-toggle__scenery">
              <div className="bb8-toggle__star"></div>
              <div className="bb8-toggle__star"></div>
              <div className="bb8-toggle__star"></div>
              <div className="bb8-toggle__star"></div>
              <div className="bb8-toggle__star"></div>
              <div className="bb8-toggle__star"></div>
              <div className="bb8-toggle__star"></div>
              <div className="tatto-1"></div>
              <div className="tatto-2"></div>
              <div className="gomrassen"></div>
              <div className="hermes"></div>
              <div className="chenini"></div>
              <div className="bb8-toggle__cloud"></div>
              <div className="bb8-toggle__cloud"></div>
              <div className="bb8-toggle__cloud"></div>
            </div>
            <div className="bb8">
              <div className="bb8__head-container">
                <div className="bb8__antenna"></div>
                <div className="bb8__antenna"></div>
                <div className="bb8__head"></div>
              </div>
              <div className="bb8__body"></div>
            </div>
            <div className="artificial__hidden">
              <div className="bb8__shadow"></div>
            </div>
          </div>
        </label> */}
        <Badge tone={userData?.role === "USER" ? "info" : "magic"}>
          {userData?.role}
        </Badge>
      </div>
      <TopBar.UserMenu

        actions={userMenuActions}
        name={userData?.first_name + " " + userData?.last_name}
        detail={userData?.shop?.name}
        initials={
          userData?.first_name?.charAt(0)?.toUpperCase() +
          "" +
          userData?.last_name?.charAt(0)?.toUpperCase()
        }
        open={userMenuActive}
        onToggle={toggleUserMenuActive}
      />
    </>
  );

  const searchResultsMarkup = (
    <ActionList
      items={[
        { content: "Shopify help center" },
        { content: "Community forums" },
      ]}
    />
  );

  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      userMenu={userMenuMarkup}
      searchResultsVisible={searchActive}
      searchResults={searchResultsMarkup}
      onSearchResultsDismiss={handleSearchResultsDismiss}
      onNavigationToggle={toggleMobileNavigationActive}
    />
  );
  const navigationMarkup = (
    <Navigation location="/">
      <Navigation.Section
        fill
        separator={false}
        items={[
          {
            label: "Analytics",
            icon: ChartVerticalIcon,
            url: "/dashboard",
            onClick: () => navigateTo("/dashboard"),
            selected: pageLocation.pathname === "/dashboard",
            disabled: !READ_DASHBOARD_ACCESS && userData?.role !== "ADMIN",
            subNavigationItems: [
              {
                url: "/dashboard/staff-analytics",
                label: "Staff Analytics",
                onClick: () => navigateTo("/dashboard/staff-analytics"),
                disabled: false,
                exactMatch: true,
                matches: pageLocation.pathname === "/dashboard/staff-analytics",
              },
              {
                url: "/dashboard/couriers-analytics",
                label: "Couriers Analytics",
                onClick: () => navigateTo("/dashboard/couriers-analytics"),
                disabled: false,
                exactMatch: true,
                matches: pageLocation.pathname === "/dashboard/couriers-analytics",
              },
            ],
          },
          {
            label: "Orders",
            icon: OrderIcon,
            onClick: () => navigateTo("/dashboard/orders"),
            selected: pageLocation.pathname === "/dashboard/orders",
            disabled: !READ_ORDERS_ACCESS && userData?.role !== "ADMIN",
          },
          {
            label: "Finance",
            icon: ReceiptPaidIcon,
            onClick: () => navigateTo("/dashboard/finance"),
            selected: pageLocation.pathname === "/dashboard/finance",
            disabled: !READ_FINANCE_ACCESS && userData?.role !== "ADMIN",
          },
          {
            label: "Accounts",
            icon: LiveIcon,
            onClick: () => navigateTo("/dashboard/accounts"),
            selected:
              pageLocation.pathname === "/dashboard/accounts/create-account" ||
              pageLocation.pathname === "/dashboard/accounts",
            disabled: !READ_ACCOUNT_ACCESS && userData?.role !== "ADMIN",
          },
          {
            label: "Cheque Uploader",
            icon: FileIcon,
            onClick: () => navigateTo("/dashboard/cheque-uploader"),
            selected: pageLocation.pathname === "/dashboard/cheque-uploader",
            disabled: !READ_PAYMENT_ACCESS && userData?.role !== "ADMIN",
          },
          {
            label: "Staff",
            icon: PersonSegmentIcon,
            badge: (
              <Badge tone="enabled" icon={LockIcon}>
                Admin only
              </Badge>
            ),
            url: "/dashboard/users",
            onClick: () => navigateTo("/dashboard/users"),
            selected:
              pageLocation.pathname === "/dashboard/users" ||
              pageLocation.pathname === "/dashboard/create-user",
            disabled: userData?.role !== "ADMIN",
            // subNavigationItems: [
            //   {
            //     label: "Staff stats",
            //     url: "/dashboard/staff",
            //     onClick: () => navigateTo("/dashboard/staff"),
            //     disabled: false,
            //     exactMatch: true,
            //     matches: pageLocation.pathname === "/dashboard/staff",
            //   },
            // ],
          },
          {
            label: "Tracking",
            icon: LiveIcon,
            onClick: () => navigateTo("/dashboard/tracking"),
            selected: pageLocation.pathname === "/dashboard/tracking",
            disabled: !TRACK_ORDERS_ACCESS && userData?.role !== "ADMIN",
          },
          {
            label: "Shopify orders",
            icon: OrderIcon,
            onClick: () => navigateTo("/dashboard/shopify/orders"),
            selected: pageLocation.pathname === "/dashboard/shopify/orders",
            disabled: !READ_SHOPIFY_ORDERS_ACCESS && userData?.role !== "ADMIN",
            badge: (
              <Badge tone="attention" icon={StarIcon}>
                New
              </Badge>
            ),
          },
          {
            label: "Docs",
            icon: ContractIcon,
            url: "/dashboard/docs/dashboard",
            onClick: () => navigateTo("/dashboard/docs/dashboard"),
            selected: pageLocation.pathname === "/dashboard/docs/dashboard",
            subNavigationItems: [
              {
                url: "/dashboard/docs/dashboard",
                label: "Dashboard",
                onClick: () => navigateTo("/dashboard/docs/dashboard"),
                disabled: false,
                exactMatch: true,
                matches: pageLocation.pathname === "/dashboard/docs/dashboard",
              },
              {
                url: "/dashboard/docs/staff-analytics",
                label: "Staff Analytics",
                onClick: () => navigateTo("/dashboard/docs/staff-analytics"),
                disabled: false,
                exactMatch: true,
                matches: pageLocation.pathname === "/dashboard/docs/staff-analytics",
              },
              {
                url: "/dashboard/docs/couriers-analytics",
                label: "Couriers Analytics",
                onClick: () => navigateTo("/dashboard/docs/couriers-analytics"),
                disabled: false,
                exactMatch: true,
                matches: pageLocation.pathname === "/dashboard/docs/couriers-analytics",
              },
              {
                url: "/dashboard/docs/orders",
                label: "Orders",
                onClick: () => navigateTo("/dashboard/docs/orders"),
                disabled: false,
                exactMatch: true,
                matches: pageLocation.pathname === "/dashboard/docs/orders",
              },
              {
                url: "/dashboard/docs/live-tracking",
                label: "Live tracking",
                onClick: () => navigateTo("/dashboard/docs/live-tracking"),
                disabled: false,
                exactMatch: true,
                matches:
                  pageLocation.pathname === "/dashboard/docs/live-tracking",
              },
              {
                url: "/dashboard/docs/tracking",
                label: "Tracking",
                onClick: () => navigateTo("/dashboard/docs/tracking"),
                disabled: false,
                exactMatch: true,
                matches: pageLocation.pathname === "/dashboard/docs/tracking",
              },
              {
                url: "/dashboard/docs/tracking-accounts",
                label: "Tracking accounts",
                onClick: () => navigateTo("/dashboard/docs/tracking-accounts"),
                disabled: false,
                exactMatch: true,
                matches:
                  pageLocation.pathname === "/dashboard/docs/tracking-accounts",
              },
              {
                url: "/dashboard/docs/cheque-uploader",
                label: "Cheque Uploader",
                onClick: () => navigateTo("/dashboard/docs/cheque-uploader"),
                disabled: false,
                exactMatch: true,
                matches:
                  pageLocation.pathname === "/dashboard/docs/cheque-uploader",
              },
              {
                url: "/dashboard/docs/preferences",
                label: "Preferences",
                onClick: () => navigateTo("/dashboard/docs/preferences"),
                disabled: false,
                exactMatch: true,
                matches: pageLocation.pathname === "/dashboard/docs/preferences",
              },
              {
                url: "/dashboard/docs/access-scopes",
                label: "Access Scopes",
                onClick: () => navigateTo("/dashboard/docs/access-scopes"),
                disabled: false,
                exactMatch: true,
                matches: pageLocation.pathname === "/dashboard/docs/access-scopes",
              },
              {
                url: "/dashboard/docs/order-booking",
                label: "Order booking",
                onClick: () => navigateTo("/dashboard/docs/order-booking"),
                disabled: false,
                exactMatch: true,
                matches:
                  pageLocation.pathname === "/dashboard/docs/order-booking",
              },
              {
                url: "/dashboard/docs/merge-orders",
                label: "Merge orders",
                onClick: () => navigateTo("/dashboard/docs/merge-orders"),

                matches:
                  pageLocation.pathname === "/dashboard/docs/merge-orders",
              },
              {
                url: "/dashboard/docs/settings",
                label: "Settings",
                onClick: () => navigateTo("/dashboard/docs/settings"),
                disabled: false,
                exactMatch: true,
                matches: pageLocation.pathname === "/dashboard/docs/settings",
              },
              {
                url: "/dashboard/docs/users",
                label: "User management",
                onClick: () => navigateTo("/dashboard/docs/users"),
                disabled: false,
                exactMatch: true,
                matches: pageLocation.pathname === "/dashboard/docs/users",
              },
              {
                url: "/dashboard/docs/two-factor-auth",
                label: "Two factor auth",
                onClick: () => navigateTo("/dashboard/docs/two-factor-auth"),
                disabled: false,
                exactMatch: true,
                matches:
                  pageLocation.pathname === "/dashboard/docs/two-factor-auth",
              },
            ],
          },
          {
            label: "Plans",
            icon: ReceiptPaidIcon,
            onClick: () => navigateTo("/dashboard/plans"),
            selected: pageLocation.pathname === "/dashboard/plans",
            disabled: userData?.role !== "ADMIN",
            badge: (
              <Badge tone="enabled" icon={LockIcon}>
                Admin only
              </Badge>
            ),
          },
          {
            label: "Settings",
            icon: SettingsIcon,
            onClick: () => navigateTo("/dashboard/settings"),
            selected: pageLocation.pathname === "/dashboard/settings",
            disabled: !READ_SETTINGS_ACCESS && userData?.role !== "ADMIN",
          },
          {
            label: "Export rules",
            icon: ExportIcon,
            onClick: () => navigateTo("/dashboard/export-rules"),
            selected: pageLocation.pathname === "/dashboard/export-rules",
            disabled:userData?.role !== "ADMIN",
            badge: (
              <Badge tone="enabled" icon={LockIcon}>
                Admin only
              </Badge>
            ),
          },
          {
            label: "Preferences",
            icon: CursorOptionIcon,
            onClick: () => navigateTo("/dashboard/preferences"),
            selected: pageLocation.pathname === "/dashboard/preferences",
            disabled: false,
          },
          {
            label: "Support",
            icon: QuestionCircleIcon,
            onClick: () => navigateTo("/dashboard/support"),
            selected: pageLocation.pathname === "/dashboard/support",
          },
        ]}
      />
      <Navigation.Section
        items={[
          {
            label: "Profile",
            icon: ProfileIcon,
            onClick: () => navigateTo("/dashboard/profile"),
            selected: pageLocation.pathname === "/dashboard/profile",
          },
          {
            label: "Logout",
            icon: ExitIcon,
            onClick: () => {
              onLogoutHandler(event);
            },

          },
        ]}
      ></Navigation.Section>
    </Navigation>
  );
  const logo = {
    topBarSource:
      "https://cdn.shopify.com/s/files/1/0791/8495/3562/files/BookEase-removebg-preview.png?v=1763035047",
    contextualSaveBarSource:
      "https://cdn.shopify.com/s/files/1/0372/5675/7381/files/BookEase.png?v=1730282412",
    accessibilityLabel: "Shopify",
  };
  
  const darkThemeStyles = `
  .Polaris-Navigation__PrimaryNavigation,  .Polaris-TopBar,  .Polaris-IndexTable__TableHeading {
    background-color: rgba(48, 48, 48, 1) !important;
  }

  .Polaris-TopBar-Menu__Activator--userMenu,
  .Polaris-Text--root.Polaris-Text--bodySm.Polaris-Text--medium.Polaris-Text--block.Polaris-Text--start.Polaris-Text--truncate,
  .Polaris-Text--root.Polaris-Text--bodyXs.Polaris-Text--block.Polaris-Text--start.Polaris-Text--textInverseSecondary.Polaris-Text--truncate,
  .Polaris-TextField.Polaris-TextField--borderless.Polaris-TextField--slim {
    background-color: rgba(48, 48, 48, 1) !important;
    color: white !important;
  }

  .Polaris-Navigation__Item,  .link_text_color .Polaris-Link {
    color: #e3e3e3 !important;
  }

  .Polaris-Link:hover, .Polaris-Link {
    color: rgb(147 194 255);
    text-decoration: underline;
}

  .table-bottom-banner, .Polaris-DropZone__Container, .Polaris-Navigation__ItemInnerWrapper.Polaris-Navigation__ItemInnerWrapper--hovered, .dashboard-skeleton, .Polaris-Navigation__ItemInnerWrapper.Polaris-Navigation__ItemInnerWrapper--selected {
    background: black !important;
  }

.recharts-cartesian-grid path{
  stroke: #646464 !important;
  fill: black !important;
}

.Polaris-Navigation__ItemInnerWrapper .Polaris-Icon__Svg{
  fill: white !important;
}

.Polaris-Badge.Polaris-Badge--toneMagic {
    background-color: rgb(56 0 135);
    color: rgb(243 243 243);
}

.Polaris-Badge--toneInfo {
    background-color: rgb(4 59 109);
    color: rgb(255 255 255);
}

.Polaris-Badge--toneCritical {
    background-color: rgb(75 0 10);
    color: rgb(255 255 255);
}

.Polaris-Badge--toneSuccess {
    background-color: rgb(3 69 16);
    color: rgb(237 237 237);
}

.Polaris-Badge--toneAttention {
    background-color: rgb(112 96 0);
    color: rgb(255 255 255);
}

 .recharts-default-tooltip {
  background-color: #1a1a1a !important;
  border: 1px solid #646464 !important;
  color: #e3e3e3 !important;
}
 .recharts-default-tooltip span{
  color: #e3e3e3 !important;
}



  .Polaris-IndexTable__ZebraStriping
  .Polaris-IndexTable__Table--scrolling
  .Polaris-IndexTable__TableRow:nth-child(2n),
  .Polaris-IndexTable__ZebraStriping
  .Polaris-IndexTable__TableRow:nth-child(2n),
  .Polaris-IndexTable__ZebraStriping
  .Polaris-IndexTable__Table--scrolling
  .Polaris-IndexTable__TableRow:nth-child(2n)
  .Polaris-IndexTable__TableCell:first-child,
  .Polaris-IndexTable__TableCell,
  .Polaris-IndexTable__ZebraStriping
  .Polaris-IndexTable__TableRow:nth-child(2n)
  .Polaris-IndexTable__TableCell:first-child,
  .Polaris-IndexTable__ZebraStriping
  .Polaris-IndexTable__Table--scrolling
  .Polaris-IndexTable__TableRow:nth-child(2n)
  .Polaris-IndexTable__TableCell--first,
  .Polaris-IndexTable__ZebraStriping
  .Polaris-IndexTable__TableRow:nth-child(2n)
  .Polaris-IndexTable__TableCell--first,
  .Polaris-IndexTable__ZebraStriping
  .Polaris-IndexTable__Table--scrolling
  .Polaris-IndexTable__TableRow:nth-child(2n)
  .Polaris-IndexTable__TableCell--first + .Polaris-IndexTable__TableCell,
  .Polaris-IndexTable__ZebraStriping
  .Polaris-IndexTable__TableRow:nth-child(2n)
  .Polaris-IndexTable__TableCell--first + .Polaris-IndexTable__TableCell,
  .Polaris-IndexTable__ZebraStriping
  .Polaris-IndexTable__Table--scrolling
  .Polaris-IndexTable__TableRow:nth-child(2n)
  .Polaris-IndexTable__TableCell:last-child,
  .Polaris-IndexTable__ZebraStriping
  .Polaris-IndexTable__TableRow:nth-child(2n)
  .Polaris-IndexTable__TableCell:last-child,
  .Polaris-Frame__TopBar,
  .Polaris-TextField__Backdrop,
  .Polaris-Select__Input,
  .Polaris-Select__Backdrop,
  .Polaris-IndexTable__LoadingPanel,
  .Polaris-IndexTable__LoadingPanelRow{
    background-color: #1a1a1a !important;
    border-bottom: 1px solid #646464 !important;
  }
   .Polaris-Listbox-TextOption.Polaris-Listbox-TextOption--selected{
    background-color: #1a1a1a !important;
    border-bottom: 1px solid #646464 !important;
  }
  .Polaris-Listbox-TextOption{
  background-color: rgba(48, 48, 48, 1)  !important;
  }
  .Polaris-Listbox-TextOption:hover, .scope_heading_wrapper:hover{
  background-color: rgb(63, 62, 62)  !important;
  }
    .Polaris-Page-Header__BreadcrumbWrapper button{
    background-color: rgba(48, 48, 48, 1)  !important;
    }
    .Polaris-Page-Header__BreadcrumbWrapper button:hover{
      scale: 1.1 !important;
      transition: 1s !important;
    }

  .order_status_wrapper .order_status_inner .not_active,
  .order_status_wrapper .order_status_inner::before, .Polaris-LegacyCard--subdued, .Polaris-DropZone-FileUpload {
    background-color: black;
  }

  .Polaris-Navigation__Item:hover,
  .Polaris-Navigation__Item--selected:hover,
  .Polaris-Navigation__Item--selected:focus-visible,
  .Polaris-Navigation__Item:focus-visible {
    background-color: rgb(100, 100, 100) !important;
  }

  .order_status_wrapper .order_status_inner .order_status_digit_box:first-child::after,
  .order_status_wrapper .order_status_inner .order_status_digit_box:nth-child(2)::after,
  .order_status_wrapper .order_status_inner .order_status_digit_box:nth-child(3)::after,
  .order_status_wrapper .order_status_inner .order_status_digit_box:nth-child(4)::after {
    color: white;
  }


  .Polaris_blue_text_should_be_light_green{}

  /* AI Suggestions Dark Theme */
  .ai-suggestions-dropdown {
    background-color: #1a1a1a !important;
    border: 1px solid #646464 !important;
  }

  .ai-suggestions-header {
    color: #e3e3e3 !important;
  }

  .ai-suggestion-item {
    background-color: #1a1a1a !important;
    border-bottom: 1px solid #3a3a3a !important;
  }

  .ai-suggestion-item:hover {
    background-color: rgb(63, 62, 62) !important;
  }

  .ai-suggestion-icon {
    color: #e3e3e3 !important;
  }

  .ai-suggestion-text {
    color: #e3e3e3 !important;
  }
    .recharts-text.recharts-label, .recharts-rectangle.recharts-tooltip-cursor{
      fill: white !important;
    }
    .recharts-rectangle.recharts-tooltip-cursor{
      fill: black !important;
    }
`;

  const handleStoreSelect = useCallback(async (storeId: string) => {
    try {
      const response = await switchStore(storeId).unwrap();
      if(response.two_factor_await) {
        localStorage.setItem("auth_token", response.auth_token);
        localStorage.setItem("two_factor_token", response.two_factor_token);
        navigateTo(`/two-factor?two_factor_token=${response.two_factor_token}`);
        return;
      }
      // Save new tokens to localStorage
      if (response.token) {
        localStorage.setItem("auth_token", response.token);
      }
      if (response.refreshToken) {
        localStorage.setItem("refresh_token", response.refreshToken);
      }

      // Update user data in localStorage and Redux
      if (response.user) {
        localStorage.setItem("user", JSON.stringify(response.user));
        // Dispatch action to update Redux state if needed
        // You may need to create a setUser action in AuthSlice
      }

      // Show success message
      dispatch(
        showToast({
          message: response.message || "Successfully switched store",
          error: false,
          visible: true,
        })
      );

      // Close modal
      setStoreModalActive(false);

      // Refresh the page to reflect new store context
      window.location.reload();
    } catch (error: any) {
      // Handle error
      dispatch(
        showToast({
          message: error?.data?.message || error?.message || "Failed to switch store",
          error: true,
          visible: true,
        })
      );
    }
  }, [switchStore, dispatch]);

  return (
    <div style={{ height: "500px" }}>
      {theme === "dark" && <style>{darkThemeStyles}</style>}
      <Modal
        open={storeModalActive}
        onClose={() => !isSwitchingStore && setStoreModalActive(false)}
        title="Select the store"
        primaryAction={{
          content: "Close",
          onAction: () => setStoreModalActive(false),
          disabled: isSwitchingStore,
        }}
      >
        <Modal.Section>
          <BlockStack gap="300">
            {isSwitchingStore && (
              <div style={{ padding: "16px", display: "flex", alignItems: "center", justifyContent: "center", gap: "12px" }}>
                <Spinner size="small" />
                <Text tone="subdued" as="p" variant="bodyMd">
                  Switching store...
                </Text>
              </div>
            )}
            {storesLoading ? (
              <div style={{ padding: "16px", display: "flex", alignItems: "center", justifyContent: "center", gap: "12px" }}>
                <Spinner size="small" />
                <Text tone="subdued" as="p" variant="bodyMd">
                  Loading stores...
                </Text>
              </div>
            ) : stores.length > 0 ? (
              stores.map((store: any) => (
                <div
                  key={store._id}
                  onClick={() => !store.uninstalled && !store.is_logged_in && !isSwitchingStore && handleStoreSelect(store._id)}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                    padding: "16px",
                    borderRadius: "8px",
                    cursor: store.uninstalled || store.is_logged_in || isSwitchingStore ? "default" : "pointer",
                    opacity: store.uninstalled || isSwitchingStore ? 0.6 : 1,
                    transition: "background-color 0.2s",
                    border: store.is_logged_in ? (theme === "dark" ? "1px solid #008060" : "1px solid #008060") : "1px solid transparent",
                    backgroundColor: store.is_logged_in ? (theme === "dark" ? "rgba(0, 128, 96, 0.1)" : "rgba(0, 128, 96, 0.05)") : "transparent",
                  }}
                  onMouseEnter={(e) => {
                    if (!store.uninstalled && !store.is_logged_in) {
                      e.currentTarget.style.backgroundColor = theme === "dark" ? "#2a2a2a" : "#f6f6f7";
                      e.currentTarget.style.borderColor = theme === "dark" ? "#3a3a3a" : "#e1e3e5";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!store.is_logged_in) {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.borderColor = "transparent";
                    } else {
                      e.currentTarget.style.backgroundColor = theme === "dark" ? "rgba(0, 128, 96, 0.1)" : "rgba(0, 128, 96, 0.05)";
                      e.currentTarget.style.borderColor = theme === "dark" ? "#008060" : "#008060";
                    }
                  }}
                >
                  <div style={{ scale: "1.5", display: "flex", alignItems: "center", justifyContent: "center", }}>
                    <Icon source={StoreIcon} tone={store.uninstalled ? "subdued" : store.is_logged_in ? "magic" : "subdued"} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      <Text variant="bodyLg" fontWeight="semibold" as="p" tone={store.uninstalled ? "subdued" : "base"}>
                        {store.name || "Store"}
                      </Text>
                      {store.is_logged_in && (
                        <Badge tone="success" size="small">
                          Current
                        </Badge>
                      )}
                      {store.uninstalled && (
                        <Badge tone="critical" size="small">
                          Uninstalled
                        </Badge>
                      )}
                    </div>
                    <div style={{ marginTop: "4px" }}>
                      <Text variant="bodySm" tone="subdued" as="p">
                        {store.domain || "Switch to this store"}
                      </Text>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <Text tone="subdued" as="p">
                No stores available
              </Text>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
      <Frame
        logo={logo}
        topBar={topBarMarkup}
        navigation={navigationMarkup}
        showMobileNavigation={mobileNavigationActive}
      >
        <>
          <ToastAlert />
        </>
        <div style={{ padding: ".5rem" }}>
          <Outlet />
        </div>
      </Frame>
    </div>
  );
}
